import { useMemo } from 'react';
import { Clock, Plus, Ticket } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { MoneyValue } from './MoneyValue';
import { useAvailability } from '@/hooks/queries';
import { summarizeAvailability, type UnavailableReason } from '@/lib/slotAvailability';
import { number as formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Product } from '@/types/product';

interface ProductCardProps {
  product: Product;
  onSelect: (product: Product) => void;
  /** Stagger index for the load reveal. */
  index?: number;
  /** Date-resolved price for the visit date; overrides the base display price. */
  resolvedPrice?: number;
  /** Visit date (YYYY-MM-DD) — drives slot-based capacity + expiry. */
  visitDate: string;
}

/** Below this many tickets left, the capacity pill turns urgent (gold). */
const LOW_STOCK_THRESHOLD = 10;

const WATERMARK_LABEL: Record<UnavailableReason, string> = {
  full: 'Habis',
  expired: 'Waktu Habis',
  none: 'Tidak Tersedia',
};

export function ProductCard({
  product,
  onSelect,
  index = 0,
  resolvedPrice,
  visitDate,
}: ProductCardProps) {
  // Main entry tickets are slot-based: capacity + expiry come from the day's
  // schedule, not a stock counter. Non-slot tickets fall back to stock/soldOut.
  const slotBased = product.requiresSlot === true;
  const avail = useAvailability(product.id, visitDate, slotBased);

  const summary = useMemo(() => {
    if (!slotBased) {
      const stock = product.stock;
      const full = product.soldOut === true || stock === 0;
      return {
        state: full ? ('unavailable' as const) : ('available' as const),
        reason: full ? ('full' as UnavailableReason) : undefined,
        remaining: typeof stock === 'number' ? stock : null,
      };
    }
    // Never block on a fetch error; keep the ticket sellable and hide capacity.
    if (avail.isError) return { state: 'available' as const, remaining: null };
    // Slots still loading — leave the card enabled, no watermark yet.
    if (avail.isLoading || !avail.data) return null;
    return summarizeAvailability(avail.data.slots, { visitDate });
  }, [slotBased, product.stock, product.soldOut, avail.isError, avail.isLoading, avail.data, visitDate]);

  const unavailable = summary?.state === 'unavailable';
  const reason = summary?.reason;
  const remaining = summary?.state === 'available' ? summary.remaining : null;
  const lowStock = typeof remaining === 'number' && remaining <= LOW_STOCK_THRESHOLD;

  const discounted =
    product.isDiscountable &&
    typeof product.discountedPrice === 'number' &&
    product.discountedPrice < product.price;
  // The date-aware price the customer will actually be charged (falls back to
  // the weekday base when the resolver hasn't returned yet).
  const displayPrice = typeof resolvedPrice === 'number' ? resolvedPrice : product.price;

  return (
    <button
      type="button"
      disabled={unavailable}
      onClick={() => onSelect(product)}
      style={{ animationDelay: `${Math.min(index, 16) * 28}ms` }}
      className={cn(
        'group relative flex animate-reveal-up flex-col overflow-hidden rounded-lg border-2 border-border bg-card text-left shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-pop',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        unavailable &&
          'cursor-not-allowed hover:translate-y-0 hover:border-border hover:shadow-sm',
      )}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-secondary">
        {product.picture?.url ? (
          <img
            src={product.picture.url}
            alt={product.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-cream-100 to-cream-200 p-6">
            <img
              src="/logo-autopia-primary.png"
              alt=""
              aria-hidden
              className="max-h-12 w-auto max-w-[72%] object-contain opacity-25"
              loading="lazy"
            />
          </div>
        )}
        {product.requiresSlot && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-ink-900/70 px-2 py-0.5 text-[10px] font-semibold uppercase text-cream-50">
            <Clock className="h-3 w-3" /> Slot
          </span>
        )}
        {discounted && (
          <span className="absolute right-2 top-2">
            <Badge variant="gold">Diskon</Badge>
          </span>
        )}

        {/* Sold-out / expired watermark — hatched overlay + rotated stamp. */}
        {unavailable && (
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-[#211b17]/70">
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(135deg, rgba(255,255,255,0.06) 0 8px, rgba(255,255,255,0) 8px 16px)',
              }}
            />
            <span className="relative inline-flex -rotate-6 items-center gap-1.5 rounded-md border-2 border-cream-50 px-4 py-1 font-display text-lg font-bold uppercase tracking-widest text-cream-50">
              {reason === 'expired' && <Clock className="h-4 w-4" />}
              {WATERMARK_LABEL[reason ?? 'full']}
            </span>
          </div>
        )}

        {!unavailable && (
          <div className="absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100">
            <Plus className="h-5 w-5" />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-3">
        {product.category && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
            {product.category}
          </span>
        )}
        <h3 className="line-clamp-2 font-sans text-sm font-semibold leading-snug text-ink-900">
          {product.name}
        </h3>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-2 gap-y-1 pt-1">
          <div className="flex items-baseline gap-2">
            {discounted ? (
              <>
                <MoneyValue value={product.discountedPrice} emphasis className="text-base" />
                <MoneyValue value={product.price} strike className="text-xs" />
              </>
            ) : (
              <MoneyValue value={displayPrice} className="text-base" />
            )}
          </div>
          {/* Remaining capacity for the visit date — gold when stock runs low. */}
          {!unavailable && typeof remaining === 'number' && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                lowStock ? 'bg-[#e6b800]/20 text-gold-600' : 'bg-success/15 text-success',
              )}
            >
              <Ticket className="h-3 w-3" />
              Sisa {formatNumber(remaining)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
